import type { SidebarItem } from "../../../components/Sidebar.js";
import type { MeResponse } from "../auth/session.js";

/**
 * The single place that decides what a signed-in caller can navigate to in
 * the admin app, as a function of their real role — not a static list, and
 * not a per-page decision. Every future module (Assessments,
 * Organizations, Users, Audit, Operations) adds its own conditional entry
 * here as it's built, rather than every page independently deciding
 * whether to show itself in the nav.
 *
 * EAP-2: Leads is the first entry gated on the caller's real role, not
 * shown unconditionally — every route it links to (`GET /api/leads/search`
 * and friends) is Platform-Administrator-only (SECURITY_GUIDE.md), so
 * showing the link to a non-admin would just be a nav item that always
 * 403s. `me.isPlatformAdministrator` is the same server-computed flag
 * `useDashboardData`'s own gated sections already trust.
 */
export function adminNavItems(me: MeResponse): SidebarItem[] {
  return [
    { label: "Dashboard", to: "/admin" },
    ...(me.isPlatformAdministrator ? [{ label: "Leads", to: "/admin/leads" }] : []),
    // EAP-3: same gating reasoning as Leads above — every route it links to
    // (GET /api/assessments/search and friends) is Platform-Administrator-only
    // (SECURITY_GUIDE.md).
    ...(me.isPlatformAdministrator ? [{ label: "Assessments", to: "/admin/assessments" }] : []),
    // EAP-4: same gating reasoning as Leads/Assessments above — every route
    // it links to (GET /api/organizations/search and friends) is
    // Platform-Administrator-only (SECURITY_GUIDE.md).
    ...(me.isPlatformAdministrator ? [{ label: "Organizations", to: "/admin/organizations" }] : []),
    // EAP-5: same gating reasoning as every entry above — every route it
    // links to (GET /api/users/search and friends) is
    // Platform-Administrator-only (SECURITY_GUIDE.md).
    ...(me.isPlatformAdministrator ? [{ label: "Users", to: "/admin/users" }] : []),
    // EAP-6: same gating reasoning as every entry above — every route it
    // links to (GET /api/audit/search, GET /api/audit/export, and GET
    // /api/audit itself) is Platform-Administrator-only (SECURITY_GUIDE.md).
    ...(me.isPlatformAdministrator ? [{ label: "Audit", to: "/admin/audit" }] : []),
    // EAP-7: same gating reasoning as every entry above — GET
    // /api/operations/summary is Platform-Administrator-only
    // (SECURITY_GUIDE.md); health/readiness underneath it are role-agnostic
    // but the page itself composes every module's own data.
    ...(me.isPlatformAdministrator ? [{ label: "Operations", to: "/admin/operations" }] : []),
    // EAP-8: same gating reasoning as every entry above — GET
    // /api/reports/summary, GET /api/reports/trends, and GET
    // /api/reports/export are all Platform-Administrator-only
    // (SECURITY_GUIDE.md).
    ...(me.isPlatformAdministrator ? [{ label: "Reporting", to: "/admin/reporting" }] : []),
    // COM-1: same gating reasoning as every entry above — GET
    // /api/commercial/subscriptions/search, GET
    // /api/commercial/subscriptions/:id, and GET
    // /api/commercial/licenses/search are all Platform-Administrator-only
    // (SECURITY_GUIDE.md).
    ...(me.isPlatformAdministrator ? [{ label: "Commercial", to: "/admin/commercial" }] : []),
    // Admin Support Queue (2026-07-23 production-readiness audit,
    // DECISION_LOG.md's Workstream 15): same gating reasoning as every
    // entry above — GET /api/support-requests/search and PATCH
    // /api/support-requests/:id are both Platform-Administrator-only
    // (SECURITY_GUIDE.md).
    ...(me.isPlatformAdministrator
      ? [{ label: "Support Requests", to: "/admin/support-requests" }]
      : []),
  ];
}
